import { useEffect, useMemo, useRef, useState } from 'react';
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  FilmIcon,
  MagnifyingGlassMinusIcon,
  MagnifyingGlassPlusIcon,
  MusicalNoteIcon,
  PauseIcon,
  PhotoIcon,
  PlayIcon,
  PlusIcon,
  ScissorsIcon,
  TrashIcon,
  ChatBubbleBottomCenterTextIcon,
} from '@heroicons/react/24/outline';
import { IVideoSegment, IVideoTrack } from '@/services/videoEditorApi';
import api from '@/services/api';

const TRACK_HEADER_WIDTH = 152;
const TRACK_HEIGHT = 64;
const BASE_PX_PER_SECOND = 48;
const frameThumbnailCache = new Map<string, string>();

function formatTimelineTime(seconds: number): string {
  const safe = Math.max(0, seconds);
  const minutes = Math.floor(safe / 60);
  const secs = Math.floor(safe % 60);
  const frames = Math.floor((safe % 1) * 30);
  return `${String(minutes).padStart(2, '0')}:${String(secs).padStart(2, '0')}:${String(frames).padStart(2, '0')}`;
}

function TimelineRuler({ duration, pxPerSecond, onSeek }: { duration: number; pxPerSecond: number; onSeek: (time: number) => void }) {
  const interval = pxPerSecond >= 120 ? 1 : pxPerSecond >= 60 ? 2 : pxPerSecond >= 28 ? 5 : 10;
  const ticks = useMemo(() => {
    const values: number[] = [];
    for (let time = 0; time <= duration + interval; time += interval) values.push(time);
    return values;
  }, [duration, interval]);

  return (
    <div
      className="relative h-8 cursor-pointer border-b border-white/10 bg-[#11131a]"
      onPointerDown={(event) => {
        const rect = event.currentTarget.getBoundingClientRect();
        onSeek((event.clientX - rect.left) / pxPerSecond);
      }}
    >
      {ticks.map((time) => (
        <div key={time} className="absolute bottom-0 top-0 border-l border-white/15" style={{ left: time * pxPerSecond }}>
          <span className="absolute left-1.5 top-1 text-[10px] font-medium text-slate-500">{formatTimelineTime(time).slice(0, 5)}</span>
        </div>
      ))}
    </div>
  );
}

function VideoFrames({ projectId, segment, width }: { projectId: string; segment: IVideoSegment; width: number }) {
  const frameCount = Math.max(2, Math.min(24, Math.ceil(width / 72)));
  const [spriteUrl, setSpriteUrl] = useState<string | null>(null);
  const [requestVersion, setRequestVersion] = useState(0);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const cacheKey = `${projectId}|${segment.segmentId}|${segment.startTime}|${segment.endTime}|${frameCount}`;
    const cached = frameThumbnailCache.get(cacheKey);
    if (cached) {
      setSpriteUrl(cached);
      setFailed(false);
      return;
    }
    setSpriteUrl(null);
    setFailed(false);
    api.get(`/video-editor/${projectId}/segments/${segment.segmentId}/frames`, {
      params: { count: frameCount },
      responseType: 'blob',
    })
      .then((response) => {
        if (cancelled) return;
        const blob = response.data as Blob;
        if (!blob.size || !blob.type.startsWith('image/')) {
          setFailed(true);
          return;
        }
        const url = URL.createObjectURL(blob);
        frameThumbnailCache.set(cacheKey, url);
        while (frameThumbnailCache.size > 60) {
          const oldestKey = frameThumbnailCache.keys().next().value as string | undefined;
          if (!oldestKey) break;
          const oldestUrl = frameThumbnailCache.get(oldestKey);
          if (oldestUrl) URL.revokeObjectURL(oldestUrl);
          frameThumbnailCache.delete(oldestKey);
        }
        setSpriteUrl(url);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [projectId, segment.segmentId, segment.startTime, segment.endTime, frameCount, requestVersion]);

  if (!spriteUrl) {
    return (
      <div className="absolute inset-0 flex items-center justify-center bg-[repeating-linear-gradient(135deg,rgba(99,102,241,0.32)_0px,rgba(99,102,241,0.32)_12px,rgba(30,41,59,0.35)_12px,rgba(30,41,59,0.35)_24px)]">
        {failed ? (
          <button type="button" onClick={(event) => { event.stopPropagation(); setRequestVersion((value) => value + 1); }} className="rounded bg-black/70 px-2 py-1 text-[9px] font-medium text-slate-200 hover:bg-black/90">Retry frames</button>
        ) : (
          <span className="rounded bg-black/60 px-2 py-1 text-[9px] font-medium text-slate-200">Generating frames…</span>
        )}
      </div>
    );
  }

  return <img src={spriteUrl} alt="" draggable={false} className="absolute inset-0 h-full w-full object-fill" />;
}

function AudioWave({ src, width }: { src: string; width: number }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [peaks, setPeaks] = useState<number[]>(() => Array.from({ length: Math.max(20, Math.floor(width / 4)) }, (_, index) => 0.2 + ((index * 29) % 70) / 100));

  useEffect(() => {
    let cancelled = false;
    const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
    const load = async () => {
      try {
        let buffer: ArrayBuffer;
        if (src.startsWith('http://') || src.startsWith('https://') || src.startsWith('blob:') || src.startsWith('data:')) {
          buffer = await (await fetch(src, { mode: 'cors' })).arrayBuffer();
        } else {
          const response = await api.get(src.replace(/^\/api/, ''), { responseType: 'arraybuffer' });
          buffer = response.data as ArrayBuffer;
        }
        const decoded = await audioContext.decodeAudioData(buffer);
        if (cancelled) return;
        const channel = decoded.getChannelData(0);
        const count = Math.max(20, Math.min(240, Math.floor(width / 3)));
        const block = Math.max(1, Math.floor(channel.length / count));
        const values = Array.from({ length: count }, (_, index) => {
          let peak = 0;
          const start = index * block;
          for (let i = 0; i < block; i++) peak = Math.max(peak, Math.abs(channel[start + i] || 0));
          return peak;
        });
        const max = Math.max(...values, 0.001);
        setPeaks(values.map((value) => value / max));
      } catch {
        if (!cancelled) setPeaks(Array.from({ length: Math.max(20, Math.floor(width / 4)) }, (_, index) => 0.2 + ((index * 29) % 70) / 100));
      }
    };
    load();
    return () => {
      cancelled = true;
      audioContext.close().catch(() => undefined);
    };
  }, [src, width]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || peaks.length === 0) return;
    const context = canvas.getContext('2d');
    if (!context) return;
    context.clearRect(0, 0, width, 30);
    context.fillStyle = '#6ee7b7';
    const barWidth = Math.max(1, width / peaks.length - 1);
    peaks.forEach((peak, index) => {
      const height = Math.max(2, peak * 24);
      context.fillRect(index * (barWidth + 1), 15 - height / 2, barWidth, height);
    });
  }, [peaks, width]);

  return <canvas ref={canvasRef} width={width} height={30} className="absolute inset-x-0 bottom-0 h-[30px] w-full opacity-90" />;
}

interface DragState {
  segmentId: string;
  trackId: string;
  initialStart: number;
  clientX: number;
  moved: boolean;
}

interface EditorTimelineProps {
  tracks: IVideoTrack[];
  duration: number;
  currentTime: number;
  zoom: number;
  isPlaying: boolean;
  selectedSegmentId: string | null;
  projectId: string;
  audioSources?: Record<string, string>;
  onSeek: (time: number) => void;
  onSelect: (segmentId: string, trackId: string) => void;
  onMove: (segmentId: string, trackId: string, start: number) => void;
  onTogglePlay: () => void;
  onStep: (delta: number) => void;
  onZoomChange: (zoom: number) => void;
  onSplit: () => void;
  onDelete: () => void;
  onAddAudioTrack: () => void;
  onDeleteTrack: (trackId: string) => void;
  onAudioDrop: (mediaId: string, timelineStart: number) => void;
}

export default function EditorTimeline({
  tracks,
  duration,
  currentTime,
  zoom,
  isPlaying,
  selectedSegmentId,
  projectId,
  audioSources = {},
  onSeek,
  onSelect,
  onMove,
  onTogglePlay,
  onStep,
  onZoomChange,
  onSplit,
  onDelete,
  onAddAudioTrack,
  onDeleteTrack,
  onAudioDrop,
}: EditorTimelineProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<DragState | null>(null);
  const dragPreviewRef = useRef<number | null>(null);
  const [drag, setDrag] = useState<DragState | null>(null);
  const [dragPreviewStart, setDragPreviewStart] = useState<number | null>(null);
  const [isAudioDragOver, setIsAudioDragOver] = useState(false);
  const pxPerSecond = BASE_PX_PER_SECOND * zoom;
  const viewportWidth = scrollRef.current?.clientWidth || 800;
  const canvasWidth = Math.max(duration * pxPerSecond, viewportWidth - TRACK_HEADER_WIDTH);

  useEffect(() => {
    if (!drag) return;
    const handleMove = (event: PointerEvent) => {
      const current = dragRef.current;
      if (!current) return;
      const delta = (event.clientX - current.clientX) / pxPerSecond;
      const preview = Math.max(0, current.initialStart + delta);
      const moved = Math.abs(event.clientX - current.clientX) > 3;
      dragPreviewRef.current = preview;
      setDragPreviewStart(preview);
      if (moved && !current.moved) {
        dragRef.current = { ...current, moved: true };
        setDrag(dragRef.current);
      }
    };
    const handleUp = () => {
      const current = dragRef.current;
      const preview = dragPreviewRef.current;
      if (current?.moved && preview !== null) onMove(current.segmentId, current.trackId, preview);
      dragRef.current = null;
      dragPreviewRef.current = null;
      setDrag(null);
      setDragPreviewStart(null);
    };
    window.addEventListener('pointermove', handleMove);
    window.addEventListener('pointerup', handleUp, { once: true });
    return () => {
      window.removeEventListener('pointermove', handleMove);
      window.removeEventListener('pointerup', handleUp);
    };
  }, [Boolean(drag), onMove, pxPerSecond]);

  useEffect(() => {
    if (!isPlaying || !scrollRef.current) return;
    const viewport = scrollRef.current;
    const playheadX = TRACK_HEADER_WIDTH + currentTime * pxPerSecond;
    if (playheadX > viewport.scrollLeft + viewport.clientWidth - 80) {
      viewport.scrollLeft = Math.max(0, playheadX - viewport.clientWidth * 0.35);
    }
  }, [currentTime, isPlaying, pxPerSecond]);

  const seekFromPointer = (event: React.PointerEvent<HTMLDivElement>) => {
    const rect = event.currentTarget.getBoundingClientRect();
    onSeek((event.clientX - rect.left) / pxPerSecond);
  };

  return (
    <section className="flex h-full min-h-0 flex-col bg-[#0b0d12] text-slate-200">
      <div className="flex h-12 shrink-0 items-center justify-between border-b border-white/10 px-3">
        <div className="flex items-center gap-1">
          <button onClick={onAddAudioTrack} className="mr-2 flex items-center gap-1.5 rounded-md border border-white/10 bg-white/[0.04] px-2.5 py-1.5 text-[11px] font-medium text-slate-300 hover:border-emerald-400/40 hover:bg-emerald-500/10 hover:text-emerald-200" title="Add audio track">
            <PlusIcon className="h-3.5 w-3.5" /> Audio track
          </button>
          <button onClick={() => onStep(-1 / 30)} className="rounded-md p-1.5 text-slate-400 hover:bg-white/10 hover:text-white" title="Previous frame">
            <ChevronLeftIcon className="h-4 w-4" />
          </button>
          <button onClick={onTogglePlay} className="flex h-8 w-8 items-center justify-center rounded-full bg-white text-slate-950 hover:bg-indigo-100" title="Play or pause">
            {isPlaying ? <PauseIcon className="h-4 w-4" /> : <PlayIcon className="ml-0.5 h-4 w-4" />}
          </button>
          <button onClick={() => onStep(1 / 30)} className="rounded-md p-1.5 text-slate-400 hover:bg-white/10 hover:text-white" title="Next frame">
            <ChevronRightIcon className="h-4 w-4" />
          </button>
          <div className="ml-2 rounded-md bg-black/30 px-2.5 py-1 font-mono text-xs text-slate-300">
            <span className="text-white">{formatTimelineTime(currentTime)}</span>
            <span className="mx-1.5 text-slate-600">/</span>
            {formatTimelineTime(duration)}
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button onClick={onSplit} disabled={!selectedSegmentId} className="rounded-md p-1.5 text-slate-400 hover:bg-white/10 hover:text-white disabled:opacity-30" title="Split at playhead">
            <ScissorsIcon className="h-4 w-4" />
          </button>
          <button onClick={onDelete} disabled={!selectedSegmentId} className="rounded-md p-1.5 text-slate-400 hover:bg-red-500/15 hover:text-red-300 disabled:opacity-30" title="Delete segment">
            <TrashIcon className="h-4 w-4" />
          </button>
          <div className="mx-2 h-5 w-px bg-white/10" />
          <MagnifyingGlassMinusIcon className="h-4 w-4 text-slate-500" />
          <input
            type="range"
            min="0.35"
            max="4"
            step="0.05"
            value={zoom}
            onChange={(event) => onZoomChange(Number(event.target.value))}
            className="h-1 w-24 accent-indigo-500"
            aria-label="Timeline zoom"
          />
          <MagnifyingGlassPlusIcon className="h-4 w-4 text-slate-500" />
          <span className="ml-1 w-10 text-right text-[10px] text-slate-500">{Math.round(zoom * 100)}%</span>
        </div>
      </div>

      <div
        ref={scrollRef}
        className={`relative min-h-0 flex-1 overflow-auto overscroll-contain transition-colors ${isAudioDragOver ? 'bg-emerald-500/10 ring-2 ring-inset ring-emerald-400/60' : ''}`}
        onDragEnter={(event) => {
          if (event.dataTransfer.types.includes('application/x-prime-audio-id')) {
            event.preventDefault();
            setIsAudioDragOver(true);
          }
        }}
        onDragOver={(event) => {
          if (event.dataTransfer.types.includes('application/x-prime-audio-id')) {
            event.preventDefault();
            event.dataTransfer.dropEffect = 'copy';
            setIsAudioDragOver(true);
          }
        }}
        onDragLeave={(event) => {
          if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setIsAudioDragOver(false);
        }}
        onDrop={(event) => {
          event.preventDefault();
          setIsAudioDragOver(false);
          const mediaId = event.dataTransfer.getData('application/x-prime-audio-id');
          if (!mediaId || !scrollRef.current) return;
          const rect = scrollRef.current.getBoundingClientRect();
          const canvasX = event.clientX - rect.left + scrollRef.current.scrollLeft - TRACK_HEADER_WIDTH;
          onAudioDrop(mediaId, Math.max(0, canvasX / pxPerSecond));
        }}
      >
        {isAudioDragOver && (
          <div className="pointer-events-none sticky left-0 top-0 z-[60] flex h-0 justify-center">
            <div className="mt-12 rounded-full border border-emerald-300/40 bg-emerald-500 px-4 py-2 text-xs font-semibold text-emerald-950 shadow-xl">Drop to create a new audio track</div>
          </div>
        )}
        <div className="relative" style={{ width: TRACK_HEADER_WIDTH + canvasWidth, minHeight: 32 + Math.max(1, tracks.length) * TRACK_HEIGHT }}>
          <div className="sticky top-0 z-30 flex h-8">
            <div className="sticky left-0 z-40 flex shrink-0 items-center border-b border-r border-white/10 bg-[#11131a] px-3 text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-500" style={{ width: TRACK_HEADER_WIDTH }}>
              Tracks
            </div>
            <div style={{ width: canvasWidth }}>
              <TimelineRuler duration={duration} pxPerSecond={pxPerSecond} onSeek={onSeek} />
            </div>
          </div>

          {tracks.map((track) => (
            <div key={track.trackId} className="flex border-b border-white/[0.07]" style={{ height: TRACK_HEIGHT }}>
              <div className="sticky left-0 z-20 flex shrink-0 items-center gap-2 border-r border-white/10 bg-[#11131a] px-3" style={{ width: TRACK_HEADER_WIDTH }}>
                <div className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-md ${track.type === 'audio' ? 'bg-emerald-500/15 text-emerald-300' : track.type === 'caption' ? 'bg-amber-500/15 text-amber-300' : track.type === 'overlay' ? 'bg-fuchsia-500/15 text-fuchsia-300' : 'bg-indigo-500/15 text-indigo-300'}`}>
                  {track.type === 'audio' ? <MusicalNoteIcon className="h-4 w-4" /> : track.type === 'caption' ? <ChatBubbleBottomCenterTextIcon className="h-4 w-4" /> : track.type === 'overlay' ? <PhotoIcon className="h-4 w-4" /> : <FilmIcon className="h-4 w-4" />}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="truncate text-xs font-semibold text-slate-300">{track.name}</div>
                  <div className="mt-0.5 text-[9px] uppercase tracking-wider text-slate-600">{track.type}</div>
                </div>
                {track.type === 'audio' && (
                  <button onClick={() => onDeleteTrack(track.trackId)} className="rounded p-1 text-slate-600 hover:bg-red-500/10 hover:text-red-300" title="Delete audio track">
                    <TrashIcon className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
              <div
                className="relative bg-[#0d1016] bg-[linear-gradient(to_right,rgba(255,255,255,0.025)_1px,transparent_1px)]"
                style={{ width: canvasWidth, backgroundSize: `${pxPerSecond}px 100%` }}
                onPointerDown={seekFromPointer}
              >
                {track.segments.map((segment) => {
                  const isDragging = drag?.segmentId === segment.segmentId;
                  const start = isDragging && dragPreviewStart !== null ? dragPreviewStart : segment.timelineStart;
                  const width = Math.max(12, (segment.timelineEnd - segment.timelineStart) * pxPerSecond);
                  const selected = selectedSegmentId === segment.segmentId;
                  return (
                    <div
                      key={segment.segmentId}
                      className={`absolute bottom-1.5 top-1.5 overflow-hidden rounded-md border shadow-sm transition-[border-color,box-shadow] ${
                        selected
                          ? 'z-10 border-indigo-300 bg-indigo-500/70 shadow-[0_0_0_2px_rgba(129,140,248,0.28)]'
                          : track.type === 'audio'
                          ? 'border-emerald-500/50 bg-emerald-600/35 hover:border-emerald-300'
                          : 'border-indigo-500/45 bg-indigo-600/45 hover:border-indigo-300'
                      } ${isDragging ? 'cursor-grabbing opacity-80' : 'cursor-grab'}`}
                      style={{ left: start * pxPerSecond, width }}
                      onPointerDown={(event) => {
                        event.stopPropagation();
                        const segmentRect = event.currentTarget.getBoundingClientRect();
                        const seekTime = segment.timelineStart + (event.clientX - segmentRect.left) / pxPerSecond;
                        const nextDrag = { segmentId: segment.segmentId, trackId: track.trackId, initialStart: segment.timelineStart, clientX: event.clientX, moved: false };
                        onSelect(segment.segmentId, track.trackId);
                        onSeek(seekTime);
                        dragRef.current = nextDrag;
                        dragPreviewRef.current = segment.timelineStart;
                        setDrag(nextDrag);
                        setDragPreviewStart(segment.timelineStart);
                      }}
                      onDoubleClick={(event) => {
                        event.stopPropagation();
                        onSeek(segment.timelineStart);
                      }}
                      title={`${segment.title || 'Untitled'} · ${formatTimelineTime(segment.timelineStart)}–${formatTimelineTime(segment.timelineEnd)}`}
                    >
                      {track.type === 'video' && (
                        <VideoFrames projectId={projectId} segment={segment} width={width} />
                      )}
                      <div className="relative z-10 flex h-5 items-center gap-1.5 bg-gradient-to-b from-black/75 to-transparent px-2 text-[10px] font-semibold text-white">
                        {track.type === 'audio' ? <MusicalNoteIcon className="h-3 w-3 shrink-0" /> : track.type === 'caption' ? <ChatBubbleBottomCenterTextIcon className="h-3 w-3 shrink-0" /> : track.type === 'overlay' ? <PhotoIcon className="h-3 w-3 shrink-0" /> : <FilmIcon className="h-3 w-3 shrink-0" />}
                        <span className="truncate">{segment.title || 'Untitled clip'}</span>
                      </div>
                      {track.type === 'audio' && (audioSources[segment.segmentId] || segment.previewUrl || segment.s3Url || segment.sourceKey) && <AudioWave src={audioSources[segment.segmentId] || segment.previewUrl || segment.s3Url || segment.sourceKey!} width={width} />}
                      {track.type !== 'audio' && (
                        <div className="absolute inset-x-0 bottom-0 h-1 bg-gradient-to-r from-indigo-300/40 via-white/30 to-fuchsia-300/40" />
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}

          <div
            className="pointer-events-none absolute bottom-0 top-0 z-50 w-px bg-rose-500 shadow-[0_0_8px_rgba(244,63,94,0.75)]"
            style={{ left: TRACK_HEADER_WIDTH + Math.min(currentTime, duration) * pxPerSecond }}
          >
            <div className="absolute -left-[7px] top-0 h-3.5 w-3.5 rounded-t-sm bg-rose-500 shadow-lg" />
            <div className="absolute -left-[7px] top-3 h-0 w-0 border-l-[7px] border-r-[7px] border-t-[7px] border-l-transparent border-r-transparent border-t-rose-500" />
          </div>
        </div>
      </div>
    </section>
  );
}
